// Humanizes Supabase auth errors for user-facing display.
//
// supabase-js sometimes surfaces errors with an empty or JSON-noise `message`
// (e.g. "{}" when the underlying response was a gateway timeout with a
// non-JSON body, or when `error_description` is absent from a 4xx payload).
// Rendering those directly produces pills like "{}" or blank red bars.
// This helper maps known `code` values to friendly copy and falls back to
// a generic message when `.message` is missing or unreadable.

const KNOWN_CODES: Record<string, string> = {
  user_already_exists:        "An account with that email already exists. Try signing in.",
  email_exists:               "An account with that email already exists. Try signing in.",
  weak_password:              "Password is too weak. Use a longer, less common password.",
  same_password:              "New password must be different from the current one.",
  invalid_credentials:        "Invalid email or password.",
  email_not_confirmed:        "Please confirm your email before continuing.",
  user_not_found:             "No account found with that email.",
  email_address_invalid:      "That email address looks invalid.",
  signup_disabled:            "Account creation is currently disabled.",
  over_email_send_rate_limit: "Too many emails sent recently. Try again in a minute.",
  over_request_rate_limit:    "Too many requests. Try again in a minute.",
  captcha_failed:             "We couldn't verify you're not a bot. Please try again.",
};

const FALLBACK = "Something went wrong. Please try again.";

function isMessageGarbage(msg: unknown): boolean {
  if (typeof msg !== "string") return true;
  const trimmed = msg.trim();
  if (!trimmed) return true;
  // Pure JSON-noise: "{}", "[]", "null", or anything that's only braces/brackets/whitespace.
  if (/^[{}\[\]\s]+$/.test(trimmed)) return true;
  if (trimmed === "null" || trimmed === "undefined") return true;
  if (trimmed === "[object Object]") return true;
  return false;
}

export function formatAuthError(error: unknown): string {
  if (!error) return FALLBACK;
  const e = error as { code?: string; message?: unknown };
  if (e.code && KNOWN_CODES[e.code]) return KNOWN_CODES[e.code];
  if (!isMessageGarbage(e.message)) return e.message as string;
  return FALLBACK;
}

// Locale-aware variant. Returns either a translation `key` into the
// `authErrors` namespace (for known Supabase codes / the generic fallback) or
// a raw passthrough `message` when Supabase already supplied a real human
// string we can't translate. Callers resolve the key with their own
// translator (`t(key)` client-side, `getTranslations("authErrors")` in server
// actions). The key for a known code IS the code itself — the `authErrors`
// namespace mirrors KNOWN_CODES key-for-key, plus a "fallback".
export type ResolvedAuthError = { key: string } | { message: string };

export function resolveAuthError(error: unknown): ResolvedAuthError {
  if (!error) return { key: "fallback" };
  const e = error as { code?: string; message?: unknown };
  if (e.code && e.code in KNOWN_CODES) return { key: e.code };
  if (!isMessageGarbage(e.message)) return { message: e.message as string };
  return { key: "fallback" };
}
